import BulkEditsStep1 from "../../../../images/guide/bulk-edits-step-1.png";
import BulkEditsStep2 from "../../../../images/guide/bulk-edits-step-2.png";
import BulkEditsStep3 from "../../../../images/guide/bulk-edits-step-3.png";
import BulkEditsStep4 from "../../../../images/guide/bulk-edits-step-4.png";
import BulkEditsStep5 from "../../../../images/guide/bulk-edits-step-5.png";
import SingleCourseStep1 from "../../../../images/guide/single-course-step-1.png";
import SingleCourseStep2 from "../../../../images/guide/single-course-step-2.png";
import SingleCourseStep3 from "../../../../images/guide/single-course-step-3.png";
import SingleCourseStep4 from "../../../../images/guide/single-course-step-4.png";
import SingleCourseStep5 from "../../../../images/guide/single-course-step-5.png";
import SingleCourseStep6 from "../../../../images/guide/single-course-step-6.png";
import SingleCourseStep7 from "../../../../images/guide/single-course-step-7.png";

export const GUIDE_SECTIONS = [
	{
		id: "single-course",
		navLabel: "Manage Single Course",
		title: "Manage a Single Course Survey",
		summary:
			"Use the single-course management window when one course needs individual attention before it is submitted, updated, excluded, or removed.",
		highlights: [
			"For one-course changes such as dates, question block, instructors, combining, or instanced classes.",
			"The management window includes both the survey setup area and the instructor management area.",
			"Submitting and deleting a course submission are both handled from the same window.",
		],
		steps: [
			{
				title: "Find the course in the table",
				description: [
					"Use search, sorting, or filters to locate the course by class ID, course title, or instructor name.",
				],
				image: {
					title: "Main table overview",

					src: SingleCourseStep1,
					alt: "Two entry points for opening the course management window: the row action menu and the Manage button column.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "Open the course management window",
				description: [
					"Open a course in either of two ways: click the row and choose Manage Class from the action menu, or use the green Manage button in the right-most column.",
					"This opens the full management window for that course.",
				],
				image: {
					title: "Open course actions",

					src: SingleCourseStep2,
					alt: "Open course actions including the row action menu and the dedicated Manage button.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "Review or modify survey dates and question block",
				description: [
					"Inside the management window, use the consolidated survey schedule and question block area to adjust the survey start and end dates and the question block applied to the course.",
					"When you change the survey start date, the page automatically recalculates the survey end date. The default logic aims for a two-week(+2 days) survey window, but it will stop the survey before the exam date when needed. If you need to adjust the window to be shorter than the recommended window, please set the survey END date first, then set the desired start date.",
					"You can still fine-tune the survey end date afterward if a different end date is required.",
				],
				image: {
					title: "Survey schedule and question block",

					src: SingleCourseStep3,
					alt: "Survey schedule and question block controls.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},

			{
				title: "Manage instructor assignments",
				description: [
					"Use the instructor area to add instructors, remove instructors, and update instructor roles for the course.",
					"Each course must have at least one assigned instructor before it can be submitted.",
				],
				image: {
					title: "Instructor management area",

					src: SingleCourseStep4,
					alt: "Instructor management area controls.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "Combine courses when one survey should cover multiple courses",
				description: [
					"Use the Combining tab when the course should be evaluated together with other eligible courses.",
					"Only eligible courses appear in the combine list, and the combination must keep the necessary instructor in common across the full set before you can continue.",
					"This is also the place to review or undo an existing combination when that option is available.",
				],
				image: {
					title: "Combining workflow",

					src: SingleCourseStep5,
					alt: "Combining workflow including the Combining tab, candidate list, and selected courses panel.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
				note: "If courses do not share the required instructor coverage, they cannot be combined together.",
			},
			{
				title: "Open instanced classes when a second survey path is needed",
				description: [
					"From the same management window, use Create Instanced Class to open the instancing workflow.",
					"This is useful when the same class needs an additional survey path rather than a replacement of the original one.",
				],
				image: {
					title: "Create instanced class entry point",

					src: SingleCourseStep6,
					alt: "Create Instanced Class button inside the course management window.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "Save, submit, exclude, or delete",
				description: [
					"When you save a course for the first time, the course is submitted (OR EXCLUDED, as that requires saving too) for SPOT from this window.",
					"After a course has already been submitted or excluded, the same window also has the option to delete the submission.",
					"Deleting a course submission simply resets the course to the state it was in before it was submitted. If a course is in this unsubmitted state, it is functionally equivalent to explicitly excluding the course. Neither will get a SPOT survey. Exclusions are to ensure that course are not accidentally submitted later on.",
				],
				image: {
					title: "Submission controls",

					src: SingleCourseStep7,
					alt: "Submission controls including Save, Delete Submission/Delete Exclusion, and Exclude control.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
		],
		notes: [
			"Combined child courses are read-only in the management window until the combine is undone from the parent course.",
			"If the page shows a validation warning, resolve it before saving so the course can be submitted successfully.",
			"Courses that are come into our system as combined in Campus Solutions/BearTracks CANNOT be separated into individual courses.",
		],
	},
	{
		id: "bulk-edit",
		navLabel: "Bulk Edit",
		title: "Bulk Edit and Submit Courses",
		summary:
			"Use bulk mode when several courses need the same shared updates or are ready to be reviewed and submitted together.",
		highlights: [
			"Bulk selection is created only through the checkbox column.",
			"The bulk workflow opens in the panel beside the table after at least one course is selected.",
			"Selections remain stable while you sort or filter the table.",
		],
		steps: [
			{
				title: "Select courses with the checkbox column",
				description: [
					"Use the checkbox column to build your bulk selection.",
					"The checkbox column is separate from evaluation status and is used only for bulk selection.",
				],
				image: {
					title: "Bulk selection checkboxes",

					src: BulkEditsStep1,
					alt: "Bulk selection checkboxes in the leftmost column and multiple selected rows.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "Review the selection in the side panel",
				description: [
					"Once courses are selected, the side panel lists the selected courses and gives you the next available bulk actions.",
					"You can remove individual courses from the selection there before continuing.",
					"Choose Edit to switch the side panel into shared-edit mode, or review/submit to proceed with the courses as-is.",
				],
				image: {
					title: "Bulk selection panel",

					src: BulkEditsStep2,
					alt: "Side panel showing the selected-course list and action buttons.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "NEW: Bulk Instructor Edits",
				description: [
					"We have added the ability to apply an instructor change across the entire selection of courses.",
					"This feature is primarily used for departments that enter Course Coordinators or a similar role on many classes, that do not want a SPOT survey administered for that instructor role.",
					"Please be cautious when using this feature, as it applies the change across all selected courses and if submitted/saved, will be time consuming to undo. Always review the course list carefully before submitting the change.",
				],
				image: {
					title: "Bulk review state",

					src: BulkEditsStep3,
					alt: "Review screen showing the selected courses before bulk submission is confirmed.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},

			{
				title: "Bulk Edit Question Block / Survey Window",
				description: [
					"Similar to before, you can make edits to the question block and survey window for the selected courses. These changes will apply to ALL selected courses.",
					"The way these edits are applied has been improved so the changes are more consistent and predictable than before. You can make changes and revert them seamlessly, and the review screen will always show the current state of the courses based on your edits.",
					"When you are done editing, Apply your changesand proceed to Review / Submit.",
				],
				image: {
					title: "Bulk review state",

					src: BulkEditsStep5,
					alt: "Review screen showing the selected courses before bulk submission is confirmed.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
			{
				title: "Review / Submit",
				description: [
					"The review screen shows a summary of the selected courses' question blocks, instructors, and survey dates.",
					"If everything looks good, click 'Submit X Courses' to send the courses to SPOT. If there are any issues that would prevent successful submission, the page will show warnings for each affected course so you can resolve them before submitting.",

					"Your changes will not be saved until you click the 'Submit X Courses' button at the bottom of the review screen.",
				],
				image: {
					title: "Bulk submit confirmation",

					src: BulkEditsStep4,
					alt: "Review screen showing the selected courses before bulk submission is confirmed.",
					width: 800,
					height: 450,
					layout: "responsive",
				},
			},
		],
		notes: [
			"Bulk mode supports shared date changes, shared question block changes, shared instructor assignment changes, and reviewing/submitting the current selection.",
			"Bulk mode does not support combining courses.",
			"Bulk mode does not support creating or managing instanced classes.",
			"If you need to delete a course submission or exclusion, open that course individually in the single-course management window.",
		],
	},
	// {
	// 	id: "instancing",
	// 	navLabel: "Instancing",
	// 	title: "Create and Manage Instanced Classes",
	// 	summary:
	// 		"Instanced classes create additional course entries so you can run another SPOT survey alongside the original course instead of replacing it.",
	// 	highlights: [
	// 		"Instanced classes are duplicate course entries tied to the original class.",
	// 		"They are especially useful when different instructor-role groups need different question sets or different survey windows.",
	// 		"A common example is separating TA-focused surveys from PI-focused surveys for the same class.",
	// 	],
	// 	steps: [
	// 		{
	// 			title: "Open the instancing workflow",
	// 			description: [
	// 				"You can reach instanced classes in two ways: click a course row and choose Instanced Classes from the action menu, or open the course management window and use Create Instanced Class.",
	// 			],
	// 			image: {
	// 				title: "Instancing entry points",
	// 				caption:
	// 					"Replace with a screenshot showing both the row action entry point and the button inside the management window.",
	// 			},
	// 		},
	// 		{
	// 			title: "Choose whether the new instance keeps the same instructors",
	// 			description: [
	// 				"You can create an instanced class with the same instructor assignments as the original course, or create one with different instructors.",
	// 				"If you choose different instructors, the workflow prompts you to search for instructors and assign roles before the instance can be completed.",
	// 			],
	// 			image: {
	// 				title: "Choose same or different instructors",
	// 				caption:
	// 					"Replace with a screenshot showing the two add-instance options and the instructor-entry workflow.",
	// 			},
	// 		},
	// 		{
	// 			title: "Review the instance list and confirm",
	// 			description: [
	// 				"The instancing modal shows the original course together with any existing or newly created instances.",
	// 				"Once the new entries look correct, confirm to save them.",
	// 			],
	// 			image: {
	// 				title: "Instanced class review",
	// 				caption:
	// 					"Replace with a screenshot of the instancing table before the final confirmation.",
	// 			},
	// 		},
	// 	],
	// 	notes: [
	// 		"Instancing is not part of the bulk workflow. It must be handled from a row action or from the single-course management window.",
	// 		"Use instancing when the original course still needs its own survey and an additional survey path is also required.",
	// 	],
	// },
	// {
	// 	id: "workflow-updates",
	// 	navLabel: "Workflow Updates",
	// 	title: "Important Workflow Updates",
	// 	summary:
	// 		"The page has been updated so single-course work, bulk selection, and table navigation are separated more clearly than before.",
	// 	steps: [
	// 		{
	// 			title: "Single-course access is more explicit",
	// 			description: [
	// 				"Previously, many users mainly opened a course by clicking it directly.",
	// 				"Now, you can click a row and choose the action you need, or use the dedicated Manage button for a faster direct path into course management.",
	// 			],
	// 			image: {
	// 				title: "Updated row actions",
	// 				caption:
	// 					"Replace with a screenshot showing the row action menu together with the Manage button column.",
	// 			},
	// 		},
	// 		{
	// 			title: "Bulk selection is now fully separate from evaluation status",
	// 			description: [
	// 				"The checkbox column is reserved for bulk selection only.",
	// 				"This makes it easier to tell the difference between a course's current status and the courses you are preparing to edit together.",
	// 			],
	// 			image: {
	// 				title: "Checkbox column versus status",
	// 				caption:
	// 					"Replace with a screenshot that contrasts the checkbox column with the Evaluate status column.",
	// 			},
	// 		},
	// 		{
	// 			title: "Sorting and filtering are much more robust",
	// 			description: [
	// 				"You can sort by table columns, use per-column filters, and keep working with the same bulk selection while refining the view.",
	// 				"If filters hide selected rows, the table warns you instead of silently dropping the selection.",
	// 			],
	// 			image: {
	// 				title: "Sorting, filtering, and selection safety",
	// 				caption:
	// 					"Replace with a screenshot showing active filters and the warning that selected rows are currently hidden.",
	// 			},
	// 		},
	// 		{
	// 			title: "It is safe to sort and filter while building a bulk selection",
	// 			description: [
	// 				"The page keeps bulk selection separate from the visible table state, so sorting and filtering no longer create the same selection confusion that older workflows could cause.",
	// 				"You can continue narrowing the list without needing to rebuild your selection from scratch.",
	// 			],
	// 			image: {
	// 				title: "Bulk selection remains stable",
	// 				caption:
	// 					"Replace with a screenshot showing a preserved bulk selection while filters or sorting are active.",
	// 			},
	// 		},
	// 	],
	// },
];

export const CHANGELOG_ENTRIES = [
	{
		id: "workflow-refresh",
		navLabel: "Workflow Refresh",
		title: "SPOT Ordering workflow refresh",
		dateLabel: "Add release date",
		summary:
			"The main SPOT Ordering workflow has been reorganized so single-course actions and bulk actions are more intuitive and easier to distinguish.",
		changes: [
			"Courses can now be managed individually from a row action menu (by clicking the row, given that there is no active bulk selection) or by using the dedicated Manage button.",
			"The single-course management experience now presents survey setup, combining, instructors, and instancing more clearly in their own window.",

			"All single-course operations are now exclusively contained in the pop-up window that appears when you choose to 'Manage' a course, so there are no more separate single-course workflows hidden in the side panel.",
		],
	},
	{
		id: "selection-update",
		navLabel: "Bulk Selection",
		title:
			"Bulk selection now uses its own checkbox column. 'Evaluate' column is no longer interactive.",
		dateLabel: "Add release date",
		summary:
			"Checkboxes are now dedicated to bulk selection instead of doubling as a general status shortcut.",
		changes: [
			"The only way to create a bulk selection is now through the leftmost checkbox column, and that is the only purpose the checkbox column serves. Non-bulk selection operations that formerly utilized a checkbox have been separated out into their own feature.",
			"Bulk-selected courses open the side panel workflow for edit, review, and submission.",
			"The flow has been improved and you should have many less instances of things like the table refreshing unnecessarily, or confusion about your applied changes.",
		],
	},
	{
		id: "table-controls",
		navLabel: "Table Controls",
		title: "Sorting and filtering are more reliable during bulk work",
		dateLabel: "Add release date",
		summary:
			"Table controls are stronger and safer, especially when you are refining a bulk selection.",
		changes: [
			"Columns support stronger sorting and filtering workflows.",
			"Bulk selections persist while you sort or filter the table.",
			"The page warns you when selected rows are temporarily hidden by active filters.",
		],
	},
];
